import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, 
  Play, 
  Pause, 
  Square, 
  Upload, 
  Camera, 
  Mic,
  MicOff,
  Settings,
  CheckCircle,
  AlertTriangle,
  Target,
  TrendingUp,
  Clock,
  Star,
  Eye,
  EyeOff,
  Download,
  Share2,
  RotateCcw
} from 'lucide-react';

interface WorkoutRecording {
  id: string;
  exerciseId: string;
  exerciseName: string;
  timestamp: Date;
  duration: number; // seconds
  videoUrl: string;
  analysis?: {
    overallScore: number;
    breakdown: {
      posture: number;
      rangeOfMotion: number;
      tempo: number;
      stability: number;
      breathing: number;
      safety: number;
    };
    keyPoints: {
      positive: string[];
      needsImprovement: string[];
      critical: string[];
    };
    riskAssessment: {
      injuryRisk: 'low' | 'medium' | 'high';
      riskFactors: string[];
      recommendations: string[];
    };
  };
  status: 'recording' | 'processing' | 'analyzed' | 'error';
}

interface WorkoutRecordingPanelProps {
  userId: string;
  currentExercise: any;
  onRecordingComplete?: (recording: WorkoutRecording) => void;
  onAnalysisComplete?: (analysis: any) => void;
}

export const WorkoutRecordingPanel: React.FC<WorkoutRecordingPanelProps> = ({
  userId,
  currentExercise,
  onRecordingComplete,
  onAnalysisComplete
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordings, setRecordings] = useState<WorkoutRecording[]>([]);
  const [selectedRecording, setSelectedRecording] = useState<WorkoutRecording | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [cameraSettings, setCameraSettings] = useState({
    resolution: '720p',
    frameRate: 30,
    audio: true
  });
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: cameraSettings.resolution === '1080p' ? 1920 : 1280,
          height: cameraSettings.resolution === '1080p' ? 1080 : 720,
          frameRate: cameraSettings.frameRate
        },
        audio: cameraSettings.audio
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        
        const newRecording: WorkoutRecording = {
          id: `recording_${Date.now()}`,
          exerciseId: currentExercise?.id || 'unknown',
          exerciseName: currentExercise?.name || 'Unknown Exercise',
          timestamp: new Date(),
          duration: recordingTime,
          videoUrl: url,
          status: 'processing'
        };

        setRecordings(prev => [newRecording, ...prev]);
        setSelectedRecording(newRecording);
        
        if (onRecordingComplete) {
          onRecordingComplete(newRecording);
        }

        // Simulate analysis
        setTimeout(() => {
          analyzeRecording(newRecording.id);
        }, 3000);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
      setIsPaused(!isPaused);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      setRecordingTime(0);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Stop all tracks
      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const analyzeRecording = async (recordingId: string) => {
    // Simulate analysis - in production, this would call WorkoutFormAnalysisBee
    setTimeout(() => {
      setRecordings(prev => prev.map(recording => 
        recording.id === recordingId 
          ? {
              ...recording,
              status: 'analyzed',
              analysis: {
                overallScore: Math.floor(Math.random() * 3) + 7,
                breakdown: {
                  posture: Math.floor(Math.random() * 3) + 6,
                  rangeOfMotion: Math.floor(Math.random() * 3) + 7,
                  tempo: Math.floor(Math.random() * 3) + 6,
                  stability: Math.floor(Math.random() * 3) + 7,
                  breathing: Math.floor(Math.random() * 3) + 6,
                  safety: Math.floor(Math.random() * 3) + 7
                },
                keyPoints: {
                  positive: ['Good posture', 'Controlled movement', 'Proper breathing'],
                  needsImprovement: ['Slightly fast tempo', 'Could use more range of motion'],
                  critical: []
                },
                riskAssessment: {
                  injuryRisk: 'low',
                  riskFactors: [],
                  recommendations: ['Slow down slightly', 'Focus on full range of motion']
                }
              }
            }
          : recording
      ));

      if (onAnalysisComplete) {
        onAnalysisComplete({ recordingId, status: 'completed' });
      }
    }, 5000);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 8) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (score >= 6) return <Target className="w-4 h-4 text-yellow-600" />;
    return <AlertTriangle className="w-4 h-4 text-red-600" />;
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'style={{ background: "var(--color-background-secondary)" }}';
    }
  };

  const getRiskTextColor = (risk: string) => {
    switch (risk) {
      case 'low': return { color: 'var(--color-success)' };
      case 'medium': return { color: 'var(--color-warning)' };
      case 'high': return { color: 'var(--color-error)' };
      default: return { color: 'var(--color-text-muted)' };
    }
  };

  return (
    <div className="flex flex-col h-full sculptd-card-elevated sculptd-sheen" style={{
      boxShadow: 'var(--shadow-card-separation)',
      border: '1px solid rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div className="flex items-center justify-end sculptd-container-padding border-b" style={{ borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => setCameraSettings(prev => ({ ...prev, audio: !prev.audio }))}
          className={`p-2 rounded-lg transition-colors ${
            cameraSettings.audio 
              ? 'bg-green-100 text-green-600' 
              : ''
          }}
          style={{ 
            background: cameraSettings.audio ? 'var(--color-success-light)' : 'var(--color-background-secondary)',
            color: cameraSettings.audio ? 'var(--color-success)' : 'var(--color-text-muted)'
          }`}
          style={{ 
            color: cameraSettings.audio ? 'var(--color-success)' : 'var(--color-text-muted)'
          }}
        >
          {cameraSettings.audio ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
      </div>

      {/* Recording Controls */}
      <div className="sculptd-container-padding border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-center space-x-4">
          {!isRecording ? (
            <button
              onClick={startRecording}
              className="flex items-center space-x-3 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Camera className="w-5 h-5" />
              <span>Start Recording</span>
            </button>
          ) : (
            <>
              <button
                onClick={pauseRecording}
                className="flex items-center space-x-3 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                <span>{isPaused ? 'Resume' : 'Pause'}</span>
              </button>
              <button
                onClick={stopRecording}
                className="flex items-center space-x-3 px-4 py-2 text-white rounded-lg transition-colors"
                style={{ 
                  background: 'var(--gradient-cta-secondary)',
                  boxShadow: 'var(--shadow-lg)'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.background = 'var(--color-text)'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.background = 'var(--gradient-cta-secondary)'}
              >
                <Square className="w-4 h-4" />
                <span>Stop</span>
              </button>
            </>
          )}
        </div>

        {isRecording && (
          <div className="mt-4 text-center">
            <div className="text-2xl font-mono text-red-600">
              {formatTime(recordingTime)}
            </div>
            <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              Recording {currentExercise?.name || 'exercise'}
            </div>
          </div>
        )}
      </div>

      {/* Video Preview */}
      <div className="flex-1 sculptd-container-padding">
        <div className="relative rounded-lg overflow-hidden aspect-video" style={{ background: 'var(--color-background-dark)' }}>
          <video
            ref={videoRef}
            autoPlay
            muted
            className="w-full h-full object-cover"
          />
          {!isRecording && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm opacity-75" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Camera preview will appear here</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recordings List */}
      {recordings.length > 0 && (
        <div className="border-t p-4" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="font-medium mb-3" style={{ color: 'var(--color-text)' }}>Recent Recordings</h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {recordings.map((recording) => (
              <div
                key={recording.id}
                className="sculptd-card-elevated sculptd-sheen flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors"
                style={{
                  borderColor: selectedRecording?.id === recording.id 
                    ? 'var(--color-primary)' 
                    : 'var(--color-border)',
                  background: selectedRecording?.id === recording.id 
                    ? 'var(--color-primary-light)' 
                    : 'white'
                }}
                onMouseEnter={(e) => {
                  if (selectedRecording?.id !== recording.id) {
                    e.currentTarget.style.borderColor = 'var(--color-text-muted)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedRecording?.id !== recording.id) {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }
                }}
                onClick={() => setSelectedRecording(recording)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden" style={{ background: 'var(--color-background-secondary)' }}>
                    <video
                      src={recording.videoUrl}
                      className="w-full h-full object-cover"
                      muted
                    />
                  </div>
                  <div>
                    <div className="font-medium" style={{ color: 'var(--color-text)' }}>{recording.exerciseName}</div>
                    <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      {recording.timestamp.toLocaleDateString()} • {formatTime(recording.duration)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {recording.status === 'processing' && (
                    <div className="flex items-center space-x-1 text-blue-600">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Analyzing...</span>
                    </div>
                  )}
                  {recording.status === 'analyzed' && (
                    <div className="flex items-center space-x-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Analyzed</span>
                    </div>
                  )}
                  {recording.status === 'error' && (
                    <div className="flex items-center space-x-1 text-red-600">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Error</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {selectedRecording?.analysis && (
        <div className="border-t p-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium" style={{ color: 'var(--color-text)' }}>Form Analysis</h3>
            <button
              onClick={() => setShowAnalysis(!showAnalysis)}
              className="text-sm"
              style={{ color: 'var(--color-primary)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.color = 'var(--color-primary-dark)'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.color = 'var(--color-primary)'}
            >
              {showAnalysis ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                {getScoreIcon(selectedRecording.analysis.overallScore)}
              </div>
              <div className={`text-lg font-bold ${getScoreColor(selectedRecording.analysis.overallScore)}`}>
                {selectedRecording.analysis.overallScore}/10
              </div>
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Overall</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                {getScoreIcon(selectedRecording.analysis.breakdown.posture)}
              </div>
              <div className={`text-sm font-medium ${getScoreColor(selectedRecording.analysis.breakdown.posture)}`}>
                {selectedRecording.analysis.breakdown.posture}/10
              </div>
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Posture</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                {getScoreIcon(selectedRecording.analysis.breakdown.safety)}
              </div>
              <div className={`text-sm font-medium ${getScoreColor(selectedRecording.analysis.breakdown.safety)}`}>
                {selectedRecording.analysis.breakdown.safety}/10
              </div>
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Safety</div>
            </div>
          </div>

          <div className={`space-y-3 ${showAnalysis ? 'block' : 'hidden'}`}>
            <div>
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Positive Points</h4>
              <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                {selectedRecording.analysis.keyPoints.positive.map((point, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>Areas for Improvement</h4>
              <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-muted)' }}>
                {selectedRecording.analysis.keyPoints.needsImprovement.map((point, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <Target className="w-3 h-3 text-yellow-600" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>Risk Assessment</h4>
              <div className="flex items-center space-x-3 mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(selectedRecording.analysis.riskAssessment.injuryRisk)}`}>
                  {selectedRecording.analysis.riskAssessment.injuryRisk.toUpperCase()} RISK
                </span>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                {selectedRecording.analysis.riskAssessment.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <Star className="w-3 h-3 text-blue-600" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutRecordingPanel;
